import { createReducer, on } from '@ngrx/store';
import {
  addQuestion,
  clearActiveSectionOfQuestionnaire,
  clearQuestionnaire,
  loadingQuestionnaire,
  markModule,
  removeQuestion,
  saveAnswerMarkError,
  saveAnswerMarkSuccess,
  savingAnswerMark,
  savingModuleMark,
  savingModuleMarkError,
  setActiveSectionOfQuestionnaire,
  setAnswer,
  getSectionOfQuestionnaireByName as setSectionOfQuestionnaireByName,
  storeActiveQuestionnaireId,
  storeQuestionDict,
  storeQuestionnaire,
} from './questionnaire.actions';
import {
  addDuplicateToQuestion,
  deriveQuestionnaireState,
  getAllModuleQuestions,
  QuestionnaireState,
  updateAnswerInState,
} from '../questionnaire-utils';
import { cloneDeep } from 'lodash';
import { IModule } from '../models/questionnaire.model';
import {
  loadingSubmissionTeam,
  storeSubmissionTeam,
} from './submission.actions';

const initialState: QuestionnaireState = {
  activeQuestionnaireId: '',
  questionnaire: undefined,
  section: undefined,
  questionnaireState: {
    questions: {},
    modules: {},
  },
  questionDict: {},
  loadingQuestionnaire: {},
  savingModuleMarks: {},
  savingAnswerMarks: {},
  answerMarkErrors: {},
  moduleMarkErrors: {},
  loadingSubmissionTeam: {},
  submissionTeam: {},
};

export const questionnaireReducer = createReducer(
  initialState,
  on(storeActiveQuestionnaireId, (state, { id }) => ({
    ...state,
    activeQuestionnaireId: id,
  })),

  on(storeQuestionnaire, (state, { questionnaire }) => {
    if (Object.keys(questionnaire).length === 0) {
      return initialState;
    }

    const nextState = cloneDeep(state);

    const compositeKey = `${questionnaire.id}-${questionnaire.submissionId}`;

    delete nextState.loadingQuestionnaire[compositeKey];

    nextState.questionnaire = questionnaire;
    nextState.questionnaireState = deriveQuestionnaireState(questionnaire);

    return nextState;
  }),
  on(clearQuestionnaire, () => {
    return initialState;
  }),
  on(loadingQuestionnaire, (state, { compositeKey }) => {
    const nextState = cloneDeep(state);

    nextState.loadingQuestionnaire[compositeKey] = true;

    return nextState;
  }),
  on(storeQuestionDict, (state, { questionDict }) => ({
    ...state,
    questionDict,
  })),
  on(setActiveSectionOfQuestionnaire, (state, { index }) => {
    const module = state.questionnaire?.modules.find(
      (module) => module.index === index,
    );

    if (module) {
      return {
        ...state,
        section: module,
      };
    }
    return state;
  }),
  on(setSectionOfQuestionnaireByName, (state, { title }) => {
    if (state.questionnaire && state.questionnaire.modules) {
      const foundModule = state.questionnaire.modules.find(
        (module) => module.title.toLowerCase() === title.toLowerCase(),
      );

      if (foundModule) {
        return {
          ...state,
          section: foundModule,
        };
      }
    }
    return state;
  }),
  on(clearActiveSectionOfQuestionnaire, (state) => ({
    ...state,
    section: undefined,
  })),

  on(saveAnswerMarkSuccess, (state, { answer, mark }) => {
    return updateAnswerInState(state, answer, mark);
  }),
  on(setAnswer, (state, { answer, mark }) => {
    return updateAnswerInState(state, answer, mark);
  }),
  on(savingAnswerMark, (state, { answerId, mark }) => {
    const nextState = cloneDeep(state);

    if (!nextState.savingAnswerMarks) {
      nextState.savingAnswerMarks = {};
    }

    if (!nextState.savingAnswerMarks[answerId]) {
      nextState.savingAnswerMarks[answerId] = {
        SME: false,
        QC: false,
      };
    }

    nextState.savingAnswerMarks[answerId][mark] = true;

    return nextState;
  }),
  on(saveAnswerMarkError, (state, { answerId, mark }) => {
    const nextState = cloneDeep(state);

    if (!nextState.answerMarkErrors) {
      nextState.answerMarkErrors = {};
    }

    if (!nextState.answerMarkErrors[answerId]) {
      nextState.answerMarkErrors[answerId] = {
        SME: '',
        QC: '',
      };
    }

    nextState.savingAnswerMarks[answerId][mark] = false;
    nextState.answerMarkErrors[answerId][mark] = 'Error saving mark';

    return nextState;
  }),
  on(markModule, (state, { module, status, mark }) => {
    const nextState = cloneDeep(state);

    if (!module?.id && !module?.moduleId) {
      console.warn('No ID sent on module, skipping');
      return nextState;
    }

    if (mark) {
      nextState.savingModuleMarks[module.id || module.moduleId][mark] = false;
    }

    if (!nextState.questionnaire) {
      console.warn('No questionnaire, skipping');
      return nextState;
    }

    const stateModule = nextState.questionnaire.modules.find(
      (stateModule) =>
        (stateModule.id && module.id === stateModule.id) ||
        module.moduleId === stateModule.id,
    );

    if (!stateModule) {
      console.warn('Module not found, skipping');
      return nextState;
    }

    if (mark) {
      stateModule.subModules.forEach((subModule) => {
        nextState.savingModuleMarks[subModule.id][mark] = false;
      });
    }

    if (status.sme_mark !== undefined) {
      stateModule.smeMark = module.smeMark;
    }

    if (status.qc_mark !== undefined) {
      stateModule.qcMark = module.qcMark;
    }

    if (stateModule.smeMark && stateModule.qcMark) {
      stateModule.isComplete = true;
    }

    const questions = getAllModuleQuestions(stateModule);

    questions.forEach((question) => {
      if (!question.answer) {
        question.answer = {};
      }

      if (status.sme_mark !== undefined) {
        question.answer.smeMark = module.smeMark;
      }

      if (status.qc_mark !== undefined) {
        question.answer.qcMark = module.qcMark;
      }
    });

    nextState.questionnaireState = deriveQuestionnaireState(
      nextState.questionnaire,
    );

    return nextState;
  }),
  on(savingModuleMark, (state, { moduleId, mark }) => {
    const nextState = cloneDeep(state);

    if (!nextState.savingModuleMarks) {
      nextState.savingModuleMarks = {};
    }

    const module = nextState.questionnaire?.modules.find(
      (m) => m.id === moduleId,
    );

    if (!nextState.savingModuleMarks[moduleId]) {
      nextState.savingModuleMarks[moduleId] = {
        SME: false,
        QC: false,
      };
    }

    module?.subModules.forEach((subModule) => {
      if (!nextState.savingModuleMarks[subModule.id]) {
        nextState.savingModuleMarks[subModule.id] = {
          SME: false,
          QC: false,
        };
      }

      nextState.savingModuleMarks[subModule.id][mark] = true;
    });

    nextState.savingModuleMarks[moduleId][mark] = true;

    return nextState;
  }),
  on(savingModuleMarkError, (state, { moduleId, mark }) => {
    const nextState = cloneDeep(state);

    if (!nextState.moduleMarkErrors) {
      nextState.moduleMarkErrors = {};
    }

    if (!nextState.moduleMarkErrors[moduleId]) {
      nextState.moduleMarkErrors[moduleId] = {
        SME: '',
        QC: '',
      };
    }

    nextState.savingModuleMarks[moduleId][mark] = false;
    nextState.moduleMarkErrors[moduleId][mark] = 'Error saving mark';

    return nextState;
  }),
  on(addQuestion, (state, { questionId, subModuleId }) => {
    const nextState = cloneDeep(state);

    if (nextState.questionnaire) {
      const nextQuestionnaire = nextState.questionnaire;

      const module = nextQuestionnaire.modules.find((m) =>
        m.subModules.some((sm) => sm.id === subModuleId),
      );

      const subModule = module?.subModules.find((sm) => sm.id === subModuleId);

      if (!subModule) {
        console.warn('Could not find subModule to add question');

        return nextState;
      }

      const questionIndex = subModule.questions.findIndex(
        (q) => q.id === questionId,
      );

      if (questionIndex === -1) {
        console.warn('Could not find question to duplicate in subModule');
        return nextState;
      }

      const originalQuestion = subModule.questions[questionIndex];

      if (!originalQuestion) {
        console.warn('Could not find question to duplicate');
        return nextState;
      }

      addDuplicateToQuestion(originalQuestion);

      nextState.questionnaireState = deriveQuestionnaireState(
        nextState.questionnaire,
      );
    }

    return nextState;
  }),
  on(removeQuestion, (state, { questionId, subModuleId, orderNumber }) => {
    const nextState = cloneDeep(state);

    if (nextState.questionnaire) {
      const nextQuestionnaire = nextState.questionnaire;

      const module = nextQuestionnaire.modules.find((m) =>
        m.subModules.some((sm) => sm.id === subModuleId),
      );

      const subModule = module?.subModules.find((sm) => sm.id === subModuleId);

      if (!subModule) {
        console.warn('Could not find subModule to remove question');

        return nextState;
      }

      const questionIndex = subModule.questions.findIndex(
        (q) => q.id === questionId,
      );

      if (questionIndex === -1) {
        console.warn('Could not find question to remove in subModule');
        return nextState;
      }

      const originalQuestion = subModule.questions[questionIndex];

      if (!originalQuestion || !originalQuestion.duplicates) {
        console.warn('Could not find question to remove');
        return nextState;
      }

      originalQuestion.duplicates = originalQuestion.duplicates.filter(
        (q, index) => index !== orderNumber,
      );

      nextState.questionnaireState = deriveQuestionnaireState(
        nextState.questionnaire,
      );
    }

    if (nextState.questionnaire) {
      nextState.questionnaireState = deriveQuestionnaireState(
        nextState.questionnaire,
      );
    }
    return nextState;
  }),
  on(loadingSubmissionTeam, (state, { id }) => {
    const nextState = cloneDeep(state);
    nextState.loadingSubmissionTeam[id] = true;
    return nextState;
  }),
  on(storeSubmissionTeam, (state, { id, team }) => {
    const nextState = cloneDeep(state);
    nextState.loadingSubmissionTeam[id] = false;
    nextState.submissionTeam[id] = team;
    return nextState;
  }),
);

export function calculateTodoCount(module: IModule): number {
  const allQuestions = getAllModuleQuestions(module);

  return allQuestions.reduce((unresolvedCount, question) => {
    if (question.answer && !question.answer?.isLocked) {
      return unresolvedCount + 1;
    }
    return unresolvedCount;
  }, 0);
}

export function updateQuestionAnswerResolved(item: any, answer: any) {
  return {
    ...item,
    answer: {
      ...item.answer,
      ...answer,
      messages: item.answer.messages?.map((message: any) => ({
        ...message,
        isResolved: true,
      })),
    },
  };
}

export function updateQuestionWithMessage(question: any, message: any) {
  return {
    ...question,
    answer: {
      ...question.answer,
      messages: [...(question.answer.messages || []), message],
      isLocked: false,
    },
  };
}

export function newQuestionnaireState(nextState: any): any {
  return {
    ...nextState,
    questionnaire: nextState.questionnaire,
    questionnaireState: deriveQuestionnaireState(nextState.questionnaire),
  };
}
